/**
 * Cache invalidation helpers.
 *
 * Sprint 2.2 Fase 4: migrated to TanStack Query singleton. Behaviour
 * preserved — `invalidate(key)` does exact match, `invalidatePrefix(prefix)`
 * matches any queryKey whose first element starts with `prefix` (legacy
 * string-prefix semantics, not TanStack's default deep-equal partial match).
 *
 * Call the appropriate function immediately after any mutation so that every
 * other page sharing the same cache keys gets fresh data on its next render,
 * without requiring a manual page refresh.
 */
import { queryClient } from "./queryClient";

function _invalidateExact(key) {
  queryClient.invalidateQueries({ queryKey: [key], exact: true });
}

function _invalidatePrefix(prefix) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const first = query.queryKey?.[0];
      return typeof first === "string" && first.startsWith(prefix);
    },
  });
}

/** Trip created / updated / cancelled / deleted / approved */
export const invalidateTrips = () => {
  _invalidatePrefix("/calendar"); // CalendarPage (all months)
  _invalidatePrefix("/legs"); // FlightsPage (all filter combos)
  // Per-trip detail caches (FlightDetailModal hits /trips/{id} and stores by
  // exact key). Without this prefix sweep the modal would serve the
  // pre-mutation snapshot — e.g. an approved trip would re-open showing
  // "pending" until the cache TTL expires.
  _invalidatePrefix("/trips");
  _invalidateExact("/dashboard/stats");
  _invalidateExact("/trips/action-required");
  // Flight Readiness Engine v3.x — every trip mutation can flip a verdict.
  _invalidatePrefix("/readiness");
  _invalidateExact("/alerts");
};

/** Flight created / updated / cancelled / deleted / approved / post-flight */
export const invalidateFlights = () => {
  _invalidatePrefix("/legs");
  _invalidatePrefix("/calendar");
  _invalidatePrefix("/trips"); // /trips/{id} detail caches
  _invalidateExact("/dashboard/stats");
  _invalidateExact("/trips/action-required");
  _invalidatePrefix("/readiness");
  _invalidateExact("/alerts");
};

/** Calendar event (non-trip) created / updated / deleted */
export const invalidateEvents = () => {
  _invalidatePrefix("/calendar");
  _invalidateExact("/cyclic-events");
};

/** Aircraft created / updated / deleted; documents / inspections / pilot assignments */
export const invalidateAircraft = () => {
  _invalidateExact("/aircraft");
  _invalidateExact("/maintenance/inspections-progress");
  // Engine reads aircraft.documents + .scheduled_inspections + .status,
  // so any aircraft change can flip BLOCKED/READY in the OCC + calendar.
  _invalidatePrefix("/readiness");
  // Dashboard banner reads stats.expiring_documents (aircraft + pilot docs).
  _invalidateExact("/dashboard/stats");
};

/** Expense created / updated / deleted */
export const invalidateExpenses = () => {
  _invalidatePrefix("/expenses");
  _invalidateExact("/dashboard/stats");
};

/** Cash movement or cash concept created / updated / deleted */
export const invalidateCash = () => {
  _invalidatePrefix("/cash");
};

/** Maintenance report or inspection created / updated / deleted */
export const invalidateMaintenance = () => {
  _invalidateExact("/maintenance");
  _invalidateExact("/maintenance/inspections");
  _invalidateExact("/maintenance/inspections-progress");
  _invalidateExact("/aircraft"); // maintenance status shown on aircraft cards
  _invalidateExact("/dashboard/stats");
  _invalidatePrefix("/readiness");
};

/** AOG declared / updated / resolved */
export const invalidateAOG = () => {
  _invalidatePrefix("/aog");
  _invalidatePrefix("/calendar"); // calendar shows AOG events
  _invalidateExact("/aircraft"); // aircraft.status flips when AOG declared/lifted
  _invalidateExact("/dashboard/stats");
  _invalidatePrefix("/readiness");
};

/**
 * Flight Readiness Engine — call after ANY action taken from the OCC's
 * resolve-conflicts modal, regardless of which collection it touched.
 * Clears every slice that consumes engine verdicts so the conflict drops
 * out of the OCC, the calendar pill repaints, and the alert feed catches
 * up — the resolution propagates app-wide instead of being a local UI fix.
 */
export const invalidateReadiness = () => {
  _invalidatePrefix("/readiness");
  _invalidateExact("/dashboard/stats");
  _invalidateExact("/trips/action-required");
  _invalidateExact("/alerts");
  _invalidatePrefix("/calendar");
  // Engine actions that mutate a leg/trip (approve, set ETD, change dest,
  // modify schedule) also flip the trip's persisted state — sweep the
  // /trips/{id} detail caches so reopening the modal reads fresh data.
  _invalidatePrefix("/trips");
};

/** Alert read / deleted / mark-all-read */
export const invalidateAlerts = () => {
  _invalidateExact("/alerts");
};

/** Owner or passenger created / updated / deleted */
export const invalidateOwners = () => {
  _invalidatePrefix("/owners"); // covers /owners, /owners/limits, /owners/{id}/passengers
  // Passenger edits feed the FRE PASSENGER_PASSPORT rule. Without this, a
  // passport renewed via the master Passengers page would leave a stale
  // BLOCKED verdict on the OCC for up to the cache TTL.
  _invalidatePrefix("/readiness");
  _invalidateExact("/alerts");
};

/** Staff profile / documents / assignments updated */
export const invalidateStaff = () => {
  _invalidateExact("/staff");
  _invalidateExact("/aircraft"); // pilot assignments surface on aircraft cards
  // Dashboard banner reads stats.expiring_documents (aircraft + pilot docs).
  _invalidateExact("/dashboard/stats");
  // Crew docs feed the FRE CREW_DOC_EXPIRED rule. Same reasoning as above —
  // without this sweep, a renewal via StaffPage leaves a stale BLOCKED
  // verdict on the OCC.
  _invalidatePrefix("/readiness");
  _invalidateExact("/alerts");
};
